import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Eye, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { formatCurrency, getFocoScoreZone, getFocoScoreLabel } from '@/lib/foco-calculations';

export type TrafficLightColor = 'green' | 'yellow' | 'red';

export interface ClientWithTrafficLight {
  id: string;
  name: string;
  email: string;
  created_at: string;
  is_active: boolean;
  suspended_until: string | null;
  access_expires_at: string | null;
  suspension_reason: string | null;
  subscription_tier: 'free' | 'premium';
  subscription_expires_at: string | null;
  stripe_subscription_id: string | null;
  latestSummary: {
    foco_score: number;
    total_income: number;
    savings_rate: number;
    debt_ratio: number;
    variable_expense_ratio: number;
    real_cash_flow: number;
    year: number;
    month: number;
  } | null;
  goalsCount: number;
  transactionsCount: number;
  lastActivityDays: number;
  overallTrafficLight: TrafficLightColor;
  alertsCount: number;
}

interface ClientTrafficLightTableProps {
  clients: ClientWithTrafficLight[];
  onViewClient: (client: ClientWithTrafficLight) => void;
}

export function ClientTrafficLightTable({ clients, onViewClient }: ClientTrafficLightTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [trafficFilter, setTrafficFilter] = useState<'all' | TrafficLightColor>('all');
  const [sortBy, setSortBy] = useState<'name' | 'foco' | 'activity' | 'alerts'>('foco');

  const getTrafficLightIcon = (color: TrafficLightColor) => {
    switch (color) {
      case 'green':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'yellow':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'red':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
  };

  const getTrafficLightBadgeClass = (color: TrafficLightColor) => {
    switch (color) {
      case 'green':
        return 'bg-success/10 text-success border-success/20';
      case 'yellow':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'red':
        return 'bg-destructive/10 text-destructive border-destructive/20';
    }
  };

  const filteredClients = clients
    .filter((client) => {
      const matchesSearch =
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTraffic = trafficFilter === 'all' || client.overallTrafficLight === trafficFilter;
      return matchesSearch && matchesTraffic;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'foco':
          return (b.latestSummary?.foco_score || 0) - (a.latestSummary?.foco_score || 0);
        case 'activity':
          return a.lastActivityDays - b.lastActivityDays;
        case 'alerts':
          return b.alertsCount - a.alertsCount;
        default:
          return 0;
      }
    });

  const trafficCounts = {
    red: clients.filter((c) => c.overallTrafficLight === 'red').length,
    yellow: clients.filter((c) => c.overallTrafficLight === 'yellow').length,
    green: clients.filter((c) => c.overallTrafficLight === 'green').length,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardTitle>Clientes con Semáforo</CardTitle>
            <div className="flex gap-2">
              <Badge variant="outline" className={getTrafficLightBadgeClass('red')}>
                {trafficCounts.red} Críticos
              </Badge>
              <Badge variant="outline" className={getTrafficLightBadgeClass('yellow')}>
                {trafficCounts.yellow} Atención
              </Badge>
              <Badge variant="outline" className={getTrafficLightBadgeClass('green')}>
                {trafficCounts.green} OK
              </Badge>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={trafficFilter} onValueChange={(v) => setTrafficFilter(v as any)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Filtrar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="red">Críticos</SelectItem>
                <SelectItem value="yellow">Atención</SelectItem>
                <SelectItem value="green">OK</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Ordenar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="foco">FOCO Score</SelectItem>
                <SelectItem value="name">Nombre</SelectItem>
                <SelectItem value="activity">Actividad</SelectItem>
                <SelectItem value="alerts">Alertas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">Estado</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-center">FOCO</TableHead>
                <TableHead className="text-center">Ahorro %</TableHead>
                <TableHead className="text-center">Deuda %</TableHead>
                <TableHead className="text-center">Var. %</TableHead>
                <TableHead className="text-center">Actividad</TableHead>
                <TableHead className="text-center">Alertas</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No se encontraron clientes
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => {
                  const summary = client.latestSummary;
                  const focoZone = summary ? getFocoScoreZone(summary.foco_score) : 'critical';

                  return (
                    <TableRow key={client.id}>
                      <TableCell>
                        <div className="flex justify-center">
                          {getTrafficLightIcon(client.overallTrafficLight)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-muted-foreground">{client.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {summary ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-lg font-bold">{summary.foco_score}</span>
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                focoZone === 'liberty'
                                  ? 'border-primary text-primary'
                                  : focoZone === 'advance'
                                  ? 'border-success text-success'
                                  : focoZone === 'risk'
                                  ? 'border-warning text-warning'
                                  : 'border-destructive text-destructive'
                              }`}
                            >
                              {getFocoScoreLabel(focoZone)}
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {summary ? (
                          <Badge
                            variant="outline"
                            className={getTrafficLightBadgeClass(
                              summary.savings_rate >= 0.2
                                ? 'green'
                                : summary.savings_rate >= 0.1
                                ? 'yellow'
                                : 'red'
                            )}
                          >
                            {(summary.savings_rate * 100).toFixed(0)}%
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {summary ? (
                          <Badge
                            variant="outline"
                            className={getTrafficLightBadgeClass(
                              summary.debt_ratio <= 0.2
                                ? 'green'
                                : summary.debt_ratio <= 0.35
                                ? 'yellow'
                                : 'red'
                            )}
                          >
                            {(summary.debt_ratio * 100).toFixed(0)}%
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {summary ? (
                          <Badge
                            variant="outline"
                            className={getTrafficLightBadgeClass(
                              summary.variable_expense_ratio <= 0.3
                                ? 'green'
                                : summary.variable_expense_ratio <= 0.4
                                ? 'yellow'
                                : 'red'
                            )}
                          >
                            {(summary.variable_expense_ratio * 100).toFixed(0)}%
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className={getTrafficLightBadgeClass(
                            client.lastActivityDays <= 3
                              ? 'green'
                              : client.lastActivityDays <= 7
                              ? 'yellow'
                              : 'red'
                          )}
                        >
                          {client.lastActivityDays === 0
                            ? 'Hoy'
                            : client.lastActivityDays === 1
                            ? 'Ayer'
                            : `${client.lastActivityDays}d`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {client.alertsCount > 0 ? (
                          <Badge variant="destructive">{client.alertsCount}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewClient(client)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
